import { KnownOrderDtoStatus, OrderDto } from '@groundfloor-platform/mgmt-service-g-sdk';
import { RestError } from '@azure/core-http';
import { useImmer } from 'use-immer';
import { useMutationArchiveOrder } from '../../order-details.queries';
import { useMemo } from 'react';

export interface ArchiveOrderActionProps {
  order: OrderDto;
  onAbort(): void;
  onSuccess(): void;
}

export interface ArchiveOrderActionState {
  current: string;
  error?: RestError;
  loading: boolean;
}

interface IntentToArchive extends ArchiveOrderActionState {
  current: 'ARCHIVE_REQUEST_PENDING';
}

interface ArchiveRequestSuccess extends ArchiveOrderActionState {
  current: 'ARCHIVE_REQUEST_SUCCESS';
}

interface ArchiveRequestFailed extends ArchiveOrderActionState {
  current: 'ARCHIVE_REQUEST_FAILED';
}

export interface ArchiveOrderActionHandlers {
  onCancelIntent(): void;
  onConfirmArchiveOrder(): void;
  onSuccessArchiveOrder(): void;
}

const initialState: ArchiveOrderActionStates = {
  current: 'ARCHIVE_REQUEST_PENDING',
  loading: false
};

export type ArchiveOrderActionStates = IntentToArchive | ArchiveRequestSuccess | ArchiveRequestFailed;

export function useArchiveOrderAction({ onAbort, onSuccess, order }: ArchiveOrderActionProps) {
  const [state, setState] = useImmer<ArchiveOrderActionStates>(initialState);
  const archiveOrderMutation = useMutationArchiveOrder(order.retailer.id, order.id);

  function getIntentSentState(error?: RestError): IntentToArchive {
    return { current: 'ARCHIVE_REQUEST_PENDING', loading: true, error };
  }

  function getArchiveRequestSuccessState(): ArchiveRequestSuccess {
    return { current: 'ARCHIVE_REQUEST_SUCCESS', loading: false };
  }

  function getArchiveRequestFailedState(error: RestError): ArchiveRequestFailed {
    return { current: 'ARCHIVE_REQUEST_FAILED', error, loading: false };
  }

  const handlers: ArchiveOrderActionHandlers = {
    onCancelIntent() {
      switch (state.current) {
        case 'ARCHIVE_REQUEST_PENDING':
        case 'ARCHIVE_REQUEST_FAILED':
          if (!state.loading) {
            onAbort();
          }
          break;
        default:
          return;
      }
    },
    onConfirmArchiveOrder() {
      switch (state.current) {
        case 'ARCHIVE_REQUEST_PENDING':
          setState(getIntentSentState(state.error));
          archiveOrderMutation.mutate(undefined, {
            onSuccess() {
              setState(getArchiveRequestSuccessState());
            },
            onError(error) {
              setState(getArchiveRequestFailedState(error));
            }
          });
          break;
        default:
          return;
      }
    },
    onSuccessArchiveOrder() {
      onSuccess();
    }
  };

  const canArchive = useMemo(() => {
    return [
      KnownOrderDtoStatus.Booked,
      KnownOrderDtoStatus.Collected,
      KnownOrderDtoStatus.Cancelled,
      KnownOrderDtoStatus.Retrieved
    ].includes(order.status as KnownOrderDtoStatus);
  }, [order.status]);

  return [state, handlers, canArchive] as const;
}
